/**
 * 加載圖片文件 - 優化版
 * @param {File} file - 圖片文件
 * @returns {Promise<HTMLImageElement>} 圖片元素
 */
export const loadImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      resolve(img);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('圖片加載失敗,請確保文件是有效的圖片格式'));
    };
    
    img.src = url;
  });
};

/**
 * 調整圖片大小 - 優化版
 * @param {HTMLImageElement} img - 圖片元素
 * @param {number} width - 目標寬度
 * @param {number} height - 目標高度
 * @param {string} format - 輸出格式
 * @param {number} quality - 圖片質量
 * @returns {Promise<Blob>} 調整大小後的圖片 Blob
 */
export const resizeImage = async (img, width, height, format = 'image/jpeg', quality = 0.9) => {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { 
        alpha: format === 'image/png',
        willReadFrequently: false 
      });
      
      canvas.width = width;
      canvas.height = height;
      
      // 對於非透明格式,設置白色背景
      if (format !== 'image/png') {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // 使用高質量縮放算法
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // 繪製調整大小後的圖片
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('圖片調整大小失敗'));
          }
        },
        format,
        quality
      );
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * 轉換圖片格式 - 優化版
 * @param {HTMLImageElement} img - 圖片元素
 * @param {string} format - 目標格式
 * @param {number} quality - 圖片質量
 * @returns {Promise<Blob>} 轉換後的圖片 Blob
 */
export const convertImageFormat = async (img, format = 'image/jpeg', quality = 0.9) => {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { 
        alpha: format === 'image/png',
        willReadFrequently: false 
      });
      
      canvas.width = img.width || img.naturalWidth;
      canvas.height = img.height || img.naturalHeight;
      
      // 對於非透明格式,設置白色背景
      if (format !== 'image/png') {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // 繪製圖片
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('圖片格式轉換失敗'));
          }
        },
        format,
        quality
      );
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * 壓縮圖片 - 增強版
 * @param {HTMLImageElement} img - 圖片元素
 * @param {object} options - 壓縮選項
 * @returns {Promise<Blob>} 壓縮後的圖片 Blob
 */
export const compressImage = async (img, options = {}) => {
  const {
    format = 'image/jpeg',
    quality = 0.7,
    maxWidth = null,
    maxHeight = null
  } = options;
  
  let width = img.width || img.naturalWidth;
  let height = img.height || img.naturalHeight;
  
  // 如果設置了最大尺寸,按比例縮放
  if (maxWidth && width > maxWidth) {
    const ratio = maxWidth / width;
    width = maxWidth;
    height = Math.round(height * ratio);
  }
  
  if (maxHeight && height > maxHeight) {
    const ratio = maxHeight / height;
    height = maxHeight;
    width = Math.round(width * ratio);
  }
  
  return resizeImage(img, width, height, format, quality);
};

/**
 * 智能壓縮圖片 - 根據文件大小自動調整
 * @param {HTMLImageElement} img - 圖片元素
 * @param {number} originalSize - 原始文件大小
 * @param {string} format - 輸出格式
 * @returns {Promise<Blob>} 壓縮後的圖片 Blob
 */
export const smartCompressImage = async (img, originalSize, format = 'image/webp') => {
  let width = img.width || img.naturalWidth;
  let height = img.height || img.naturalHeight;
  let quality = 0.85;
  
  // 根據原始文件大小調整壓縮策略
  if (originalSize > 5 * 1024 * 1024) { // > 5MB
    // 大文件:更激進的壓縮
    const scale = Math.sqrt((3 * 1024 * 1024) / originalSize);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    quality = 0.75;
  } else if (originalSize > 2 * 1024 * 1024) { // > 2MB
    // 中等文件:適度壓縮
    const scale = Math.sqrt((1.5 * 1024 * 1024) / originalSize);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    quality = 0.80;
  } else if (originalSize > 1 * 1024 * 1024) { // > 1MB
    // 小文件:輕度壓縮
    quality = 0.85;
  } else {
    // 很小的文件:只降低質量
    quality = 0.90;
  }
  
  // 確保尺寸不會太小
  const minDimension = 800;
  if (width < minDimension && height < minDimension) {
    width = img.width || img.naturalWidth;
    height = img.height || img.naturalHeight;
  }
  
  return resizeImage(img, width, height, format, quality);
};

/**
 * 計算壓縮統計信息
 * @param {number} originalSize - 原始文件大小
 * @param {number} compressedSize - 壓縮後文件大小
 * @returns {object} 統計信息
 */
export const calculateCompressionStats = (originalSize, compressedSize) => {
  const ratio = Math.round((1 - (compressedSize / originalSize)) * 100);
  const savedSpace = Math.max(0, originalSize - compressedSize);
  
  return {
    ratio: ratio >= 0 ? `${ratio}%` : `+${Math.abs(ratio)}%`,
    savedSpace,
    isReduced: compressedSize < originalSize,
    originalSize,
    compressedSize
  };
};

/**
 * 批量壓縮圖片
 * @param {File[]} files - 圖片文件數組
 * @param {object} options - 壓縮選項
 * @param {Function} onProgress - 進度回調
 * @returns {Promise<Array>} 壓縮結果數組
 */
export const batchCompressImages = async (files, options = {}, onProgress = null) => {
  const results = [];
  
  for (let i = 0; i < files.length; i++) {
    try {
      const file = files[i];
      const img = await loadImage(file);
      const blob = await compressImage(img, options);
      
      results.push({
        original: file,
        compressed: blob,
        stats: calculateCompressionStats(file.size, blob.size)
      });
      
      if (onProgress) {
        onProgress(i + 1, files.length);
      }
    } catch (error) {
      console.error(`壓縮第 ${i + 1} 個文件時出錯:`, error);
      results.push({
        original: files[i],
        error: error.message
      });
    }
  }
  
  return results;
};